/**
 * `sync.studyGroups.group(id).activity.*` — activity feed reads
 * (sorted, with unread filter against the member's `lastReadAtISO`
 * cursor) plus the `log` writer.
 */

import type { SyncGroupMembership } from '@flashka/shared/sync-engine/schema/entities';
import type { SGActivityEntry } from '@flashka/shared/sync-engine/schema/group-entities';

import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useFlashkaStore } from '../internal/store/flashka-store';
import { useStudyGroupStore } from '../internal/store/study-group-store';
import { currentUserId, nowISO, sendSG } from './helpers';
import type { SGScopedGroup } from './types';

export function createActivityApi(groupId: string): SGScopedGroup['activity'] {
  return {
    use: () =>
      useStudyGroupStore(
        useShallow((s) => {
          const entries = Object.values(s.groups[groupId]?.activity ?? {});
          return entries
            .filter((e) => !e.deletedTimeISO)
            .sort((a, b) =>
              (b.createdTimeISO ?? '').localeCompare(a.createdTimeISO ?? ''),
            );
        }),
      ),
    useUnread: () => {
      const all = useStudyGroupStore(
        useShallow((s) => {
          const entries = Object.values(s.groups[groupId]?.activity ?? {});
          return entries
            .filter((e) => !e.deletedTimeISO)
            .sort((a, b) =>
              (b.createdTimeISO ?? '').localeCompare(a.createdTimeISO ?? ''),
            );
        }),
      );
      // Match against my own lastReadAtISO via groupMemberships — the
      // member's self-marked read cursor.
      const lastReadAtISO = useFlashkaStore((s) => {
        const mems = (s as unknown as { groupMemberships?: SyncGroupMembership[] })
          .groupMemberships;
        return Array.isArray(mems)
          ? mems.find((m) => m.groupId === groupId)?.lastReadAtISO
          : undefined;
      });
      return useMemo(
        () =>
          all.filter((e) => !lastReadAtISO || (e.createdTimeISO ?? '') > lastReadAtISO),
        [all, lastReadAtISO],
      );
    },
    log: (input) => {
      const uid = currentUserId();
      const now = nowISO();
      const id = input.id ?? `${input.type}_${uid}_${Date.now()}`;
      const entry: SGActivityEntry = {
        id,
        type: input.type,
        userId: uid,
        metadata: input.metadata,
        createdTimeISO: input.createdTimeISO ?? now,
        updatedAtISO: now,
      };
      useStudyGroupStore.getState().upsertActivity(groupId, entry);
      sendSG(groupId, {
        type: 'sg-activity-push',
        key: id,
        entry,
        updatedAtISO: now,
      });
      return id;
    },
  };
}
